/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024-2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025-2026 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Collapse, Form, FormGroup, Row, Col, Button, InputGroup, Image } from 'react-bootstrap';
import { SoftwareActions } from './SoftwareActions';
import { useDispatch } from 'react-redux';
import './SoftwareList.scss';
import { IPosition, IPositionFair, LocalizedText } from '@umati/dashboard-types';
import { FileUpload } from '@umati/dashboard-common';
import { Company, Software, UserClient } from 'Api/generated';
import { URLS } from 'Api/Api';
import Api from 'Api/Api';
import Select from 'react-select';
import { AllowedRoles, useAuth } from 'Contexts/Auth';

interface ISoftwareProps {
  sw: Software;
  isAdd: boolean;
  existingSoftwareIds: Software['SoftwareId'][];
  existingCompanies: Company[];
  existingUsers: UserClient[];
}

interface IImgUpload {
  logo: React.RefObject<HTMLInputElement | null>;
  screenshot: React.RefObject<HTMLInputElement | null>;
  icon: React.RefObject<HTMLInputElement | null>;
}

const Softwares = ({ sw, isAdd, existingSoftwareIds, existingCompanies, existingUsers }: ISoftwareProps) => {
  const existingSoftwareIdsSet = new Set(existingSoftwareIds);
  const [state, setState] = useState<Software>({ ...sw });
  const [newPosition, setNewPosition] = useState('fairtag');
  const [open, setOpen] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<UserClient[]>([]);
  const dispatch = useDispatch();
  const auth = useAuth();
  const isAdminOrSuperAdmin = auth.user?.Roles?.some((r) => r === 'SuperAdmin' || r === 'Admin') ?? false;

  useEffect(() => {
    setState(sw);
    if (isAdd && existingCompanies.length === 1) {
      setState({ ...sw, RelatedCompany: existingCompanies[0].Id || '' });
    }
  }, [sw]);

  useEffect(() => {
    if (!isAdd && sw.Id && isAdminOrSuperAdmin) {
      Api.Software.getSoftwareUsers(sw.Id)
        .then((res) => setAssignedUsers(res.data))
        .catch(() => console.error('Could not load software users'));
    }
  }, [sw.Id, isAdminOrSuperAdmin]);

  const logoImgUpload = `${state.Id}-logoUpload`;
  const iconImgUpload = `${state.Id}-iconUpload`;
  const screenshotImgUpload = `${state.Id}-screenshotUpload`;

  const imgUpload: IImgUpload = {
    logo: React.createRef(),
    screenshot: React.createRef(),
    icon: React.createRef(),
  };

  function onInput<K extends keyof Software>(key: K, value: Software[K]) {
    const s = { ...state, [key]: value };
    setState(s);
  }

  const isReadOnly = (field: keyof Software) => {
    const readOnlyFields = ['Id'];
    return readOnlyFields.includes(field);
  };

  const isDeletablePosition = (position: keyof IPosition) => {
    const nonDeletablePositions = ['offsite', 'offsiteClean'];
    return nonDeletablePositions.includes(position.toString());
  };

  function onPositionChanged<K extends keyof IPosition>(value: IPosition[K], key: K) {
    let pos = state.Positions;
    if (pos) {
      pos = {
        ...pos,
        [key]: value,
      };
      setState({ ...state, Positions: pos });
    }
  }

  const setManufacturer = (value: string, key: keyof LocalizedText) => {
    setState((old) => {
      return {
        ...old,
        Data: {
          ...old.Data,
          Manufacturer: {
            ...old.Data.Manufacturer,
            [key]: value,
          },
        },
      };
    });
  };

  const addNewPosition = () => {
    setState((old) => {
      const newPos: IPositionFair = {
        booth: '',
        hall: '',
      };
      const positions = old.Positions ? old.Positions : {};
      const updatedPositions = { ...positions, [newPosition]: newPos };
      return { ...old, Positions: updatedPositions };
    });
  };

  const deletePosition = (key: keyof IPosition) => {
    setState((old) => {
      const positions = old.Positions ? old.Positions : {};
      const rest = Object.fromEntries(Object.entries(positions).filter(([k]) => k !== key));
      return { ...old, Positions: rest };
    });
  };

  const deleteSoftware = () => {
    SoftwareActions.DeleteSoftware(state)(dispatch);
  };

  const getKeys = (o: object): string[] => {
    return Object.keys(o);
  };

  const saveSoftware = () => {
    SoftwareActions.UpdateSoftware(state)(dispatch);
    if (isAdminOrSuperAdmin) {
      saveAssignedUsers();
    }
  };

  const addSoftware = () => {
    const softwareToCreate = {
      ...state,
      UserIds: assignedUsers.map((u) => u.Id || ''),
    };
    SoftwareActions.CreateSoftware(softwareToCreate)(dispatch);
  };

  const saveAssignedUsers = () => {
    if (sw.Id) {
      Api.Software.putSoftwareUsers(
        sw.Id,
        assignedUsers.map((u) => u.Id || ''),
      ).catch(() => console.error('Could not save software users'));
    }
  };

  const imgUploadChange = (type: keyof IImgUpload) => {
    const upload = imgUpload[type];
    if (!upload.current || !upload.current.files || !upload.current.files[0]) {
      return;
    }
    const file = upload.current.files[0];
    if (file) {
      SoftwareActions.UploadImg(sw, file, type)(dispatch);
    }
  };

  const groupedUserOptions = [
    {
      label: 'Company Users',
      options: existingUsers
        .filter((u) => u.CompanyIds?.includes(state.RelatedCompany || ''))
        .map((u) => ({ value: u.Id || '', label: u.Username || '' })),
    },
    {
      label: 'Other Users',
      options: existingUsers
        .filter((u) => !u.CompanyIds?.includes(state.RelatedCompany || ''))
        .map((u) => ({ value: u.Id || '', label: u.Username || '', isDisabled: true })),
    },
  ];

  return (
    <li className="list-group-item" id={state.SoftwareId || undefined}>
      <b>
        {`${state.name}|${state.Id}`}{' '}
        <Button
          onClick={() => setOpen(!open)}
          aria-controls="example-collapse-text"
          variant="Light"
          aria-expanded={open}
        >
          Expand
        </Button>
      </b>
      {isAdd === false ? (
        <>
          <Button className="btn-inline right" onClick={() => saveSoftware()}>
            Save
          </Button>
          <AllowedRoles roles={['SuperAdmin', 'Admin']}>
            <Button className="btn-inline right" onClick={() => deleteSoftware()} variant="danger">
              Delete
            </Button>
          </AllowedRoles>
        </>
      ) : (
        <Button
          className="btn-inline right"
          disabled={existingSoftwareIdsSet.has(state.SoftwareId)}
          onClick={() => addSoftware()}
        >
          Add
        </Button>
      )}
      <Collapse in={open}>
        <div>
          <Form>
            {getKeys(state).map((key) => {
              const val = (state as Record<string, unknown>)[key];

              return (
                (typeof val === 'string' || typeof val === 'number' || typeof val === 'undefined') &&
                (key === 'RelatedCompany' ? (
                  <AllowedRoles roles={['SuperAdmin', 'Admin']} key={key}>
                    <>
                      {existingCompanies.length > 1 && (
                        <FormGroup as={Row}>
                          <Form.Label column sm="2">
                            {key}
                          </Form.Label>
                          <Col sm="10">
                            <Form.Select
                              name="RelatedCompanyField"
                              value={
                                state.RelatedCompany && state.RelatedCompany !== '00000000-0000-0000-0000-000000000000'
                                  ? state.RelatedCompany
                                  : 'default'
                              }
                              onChange={(e) => {
                                onInput(key as keyof Software, e.target.value);
                              }}
                            >
                              {existingCompanies.map((el) => (
                                <option key={el.Id} value={el.Id}>
                                  {el.Name}
                                </option>
                              ))}
                              <option value="default">not related</option>
                            </Form.Select>
                          </Col>
                        </FormGroup>
                      )}
                      <FormGroup as={Row}>
                        <Form.Label column sm="2">
                          Users
                        </Form.Label>
                        <Col sm="10">
                          <Select
                            closeMenuOnSelect={false}
                            value={existingUsers
                              .filter((u) => assignedUsers.some((a) => a.Id === u.Id))
                              .map((u) => ({ value: u.Id || '', label: u.Username || '' }))}
                            isMulti
                            onChange={(e) => {
                              const selected = existingUsers.filter((u) => e.some((opt) => opt.value === u.Id));
                              setAssignedUsers(selected);
                            }}
                            options={groupedUserOptions}
                          />
                        </Col>
                      </FormGroup>
                    </>
                  </AllowedRoles>
                ) : (
                  <FormGroup as={Row} key={key}>
                    <Form.Label column sm="2">
                      {key}
                    </Form.Label>
                    <Col sm="10">
                      <Form.Control
                        as="input"
                        value={val}
                        id={key}
                        onChange={(e) => {
                          onInput(key as keyof Software, e.target.value);
                        }}
                        readOnly={isReadOnly(key as keyof Software)}
                      />
                    </Col>
                  </FormGroup>
                ))
              );
            })}
          </Form>
          <b>Data</b>
          <Form>
            <Form.Group as={Row}>
              <Form.Label column sm="2">
                Manufacturer
              </Form.Label>
              <Col>
                <Form.Group as={Row}>
                  <Form.Label column sm="4">
                    locale
                  </Form.Label>
                  <Col sm="7">
                    <Form.Control
                      value={state.Data?.Manufacturer?.locale}
                      onChange={(e) => setManufacturer(e.target.value, 'locale')}
                    ></Form.Control>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row}>
                  <Form.Label column sm="4">
                    text
                  </Form.Label>
                  <Col sm="7">
                    <Form.Control
                      value={state.Data?.Manufacturer?.text}
                      onChange={(e) => setManufacturer(e.target.value, 'text')}
                    ></Form.Control>
                  </Col>
                </Form.Group>
              </Col>
              <Col sm="1"></Col>
            </Form.Group>
          </Form>
          <b>Positions</b>
          <Form>
            {state.Positions ? (
              getKeys(state.Positions).map((key) => {
                return (
                  <Form.Group key={key} as={Row}>
                    <Form.Label column sm="2">
                      {key}
                    </Form.Label>
                    {state.Positions?.[key] && typeof state.Positions[key] === 'object' ? (
                      getKeys(state.Positions[key]!).map((subkey) => {
                        return (
                          <Col key={key + subkey}>
                            <Form.Group as={Row}>
                              <Form.Label column sm="4">
                                {subkey}
                              </Form.Label>
                              <Col sm="7">
                                <Form.Control
                                  as="input"
                                  value={state.Positions?.[key]?.[subkey]}
                                  onChange={(e) =>
                                    onPositionChanged({ ...state.Positions?.[key], [subkey]: e.target.value }, key)
                                  }
                                />
                              </Col>
                            </Form.Group>
                          </Col>
                        );
                      })
                    ) : (
                      <></>
                    )}
                    <Col sm="1">
                      <span className="input-group-btn">
                        <Button
                          variant="danger"
                          disabled={isDeletablePosition(key)}
                          onClick={() => deletePosition(key)}
                          className="inline"
                        >
                          X
                        </Button>
                      </span>
                    </Col>
                  </Form.Group>
                );
              })
            ) : (
              <></>
            )}
          </Form>
          <Form>
            <InputGroup className="mb-3">
              <InputGroup.Text id="basic-addon1">Positions</InputGroup.Text>
              <Form.Control value={newPosition} onChange={(e) => setNewPosition(e.target.value)} />
              <Button onClick={addNewPosition} variant="outline-secondary">
                Add new Position
              </Button>
            </InputGroup>
          </Form>
          <Row>
            <Col xs={4} md={4}>
              <Image src={URLS.Software.getImage(state.iconUrl || '')} fluid />
            </Col>
            <Col xs={4} md={4}>
              <Image src={URLS.Software.getImage(state.logoUrl || '')} fluid />
            </Col>
            <Col xs={4} md={4}>
              <Image src={URLS.Software.getImage(state.screenshotUrl || '')} fluid />
            </Col>
          </Row>
          <Row>
            <Col xs={4} md={4}>
              <FileUpload
                accept={['.jpg', '.png', 'jpeg']}
                ref={imgUpload.icon}
                id={iconImgUpload}
                callback={imgUploadChange}
                callbackkey="icon"
              >
                Icon Upload
              </FileUpload>
            </Col>
            <Col xs={4} md={4}>
              <FileUpload
                accept={['.jpg', '.png', 'jpeg']}
                ref={imgUpload.logo}
                id={logoImgUpload}
                callback={imgUploadChange}
                callbackkey="logo"
              >
                Logo Upload
              </FileUpload>
            </Col>
            <Col xs={4} md={4}>
              <FileUpload
                accept={['.jpg', '.png', 'jpeg']}
                ref={imgUpload.screenshot}
                id={screenshotImgUpload}
                callback={imgUploadChange}
                callbackkey="screenshot"
              >
                Screenshot Upload
              </FileUpload>
            </Col>
          </Row>
          <br></br>
          <div className="list-group-item">
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(state.description || '') }}></div>
          </div>
        </div>
      </Collapse>
    </li>
  );
};

export default Softwares;
